export type AddonKey = "dialer" | "phoneEnrichment" | "phoneVerification";

export interface AddonPriceInfo {
  id: string;
  unitAmount: number | null;
  currency: string;
}

export interface AddonBilling {
  status: string | null;
  subscriptionItemId: string | null;
  priceId: string | null;
  seats: number;
  interval: "monthly" | "yearly" | null;
  unitAmount?: number | null;
  currency?: string | null;
}

export interface AddonStatus {
  key: AddonKey;
  name: string;
  description: string;
  configured: boolean;
  enabled: boolean;
  source: "subscription" | "flag" | "legacy" | null;
  billing: AddonBilling | null;
  seatLimit: number | null;
  assigned: string[];
  prices: { monthly: AddonPriceInfo | null; yearly: AddonPriceInfo | null };
}

export interface AddonStatusResult {
  addons: AddonStatus[];
  canSelfServe: boolean;
  reason?: string;
  licensedEmails: string[];
}

export interface UsageMonth {
  period: string;
  phoneLookups: number;
  phoneLookupsFound: number;
  phoneVerifications: number;
  phoneCostUsd: number;
  /** Cost grossed up for the card fee: what the customer is charged. */
  phoneBillableUsd: number;
  extensionEnrichRequests: number;
  exportRequests: number;
  exportEmailsFound: number;
  byUser: Record<string, Record<string, number>>;
}

export interface UsageReport {
  months: UsageMonth[];
  totals: Omit<UsageMonth, "period" | "byUser">;
  matchRate: number | null;
}

export interface CallerIdLabel {
  carrier: string;
  label: string | null;
  isSpam: boolean;
  source: "manual" | "probe";
  recordedAt: number;
}

export interface CallerIdRecord {
  number: string;
  users: string[];
  score: number | null;
  status: "healthy" | "watch" | "degraded" | "flagged";
  metrics: { recent_calls: number; recent_answer_rate: number | null; baseline_answer_rate: number | null; daily_volume_7d: number; reasons: string[]; insufficient: boolean };
  labels: CallerIdLabel[];
  spamLabeled: boolean;
  registeredFcrAt?: number | null;
  registeredHiyaAt?: number | null;
  computedAt: number;
}

export interface CallerIdHealth {
  enabled: boolean;
  needsScopes: boolean;
  hasDialer: boolean;
  numbers: CallerIdRecord[];
  links: Record<string, string>;
  probeConfigured: boolean;
}

export interface LinkedInAutomationSettings {
  enabled: boolean;
  actionsPerHour: number;
  activeHoursStart: number;
  activeHoursEnd: number;
  timezone: string;
  completeTasks: boolean;
  whenNotConnected: "wait" | "skip" | "reschedule" | "withdraw";
  rescheduleDays: number;
  /** "tagged": only LinkedIn steps whose task note has #auto. "all": every LinkedIn step. */
  stepSelection: "tagged" | "all";
  /** HubSpot: run every due Connection request and InMail task (what sequence LinkedIn steps create), tagged or not. */
  hubspotSequenceSteps?: boolean;
  /** Also run one-off LinkedIn tasks (made by hand, not by a sequence) due in the last 7 days. Absent: HubSpot follows hubspotSequenceSteps. */
  oneOffTasks?: boolean;
}

export interface LinkedInAction {
  id: string;
  ownerUid: string;
  ownerEmail: string;
  actionType: "connect" | "message" | "view_profile" | "withdraw" | "react";
  payload: { linkedinUrl: string; text?: string | null };
  status: "pending" | "processing" | "completed" | "failed" | "skipped";
  scheduledAfter: number;
  error?: string | null;
  outreach: { taskId: string; prospectId: string | null; prospectName?: string | null; stepType?: string | null; taskCompleted?: boolean };
  createdAt: number;
  completedAt?: number | null;
}

export interface LinkedInAutomationAnalytics {
  connectsSent: number;
  connectsAccepted: number;
  acceptanceRate: number | null;
  messagesSent: number;
  replies: number;
  replyRate: number | null;
  queue: Record<string, number>;
  byUser: Record<string, { connectsSent: number; connectsAccepted: number; messagesSent: number; replies: number }>;
}

export interface LinkedInAutomationView {
  settings: LinkedInAutomationSettings;
  eligible: boolean;
  needsScopes: boolean;
  /** Plan gating: the organization's plan leaves LinkedIn automation out (LinkedIn Integration, Everything, or the legacy Prospector). */
  planRequired?: boolean;
  queue: LinkedInAction[];
  analytics: LinkedInAutomationAnalytics;
}

// --------------------
// Insights report (phone, LinkedIn, profile refreshes, exports)
// --------------------

export interface InsightsCounters {
  phoneLookups: number;
  phoneLookupsFound: number;
  phoneVerifications: number;
  phoneManualLookups: number;
  phoneCostUsdMicros: number;
  linkedinConnectsSent: number;
  linkedinConnectsAccepted: number;
  linkedinMessagesSent: number;
  linkedinProfileViews: number;
  linkedinReplies: number;
  profileRefreshes: number;
  /** Refreshes that changed at least one field (recorded from 2026-09-27). */
  profileUpdates?: number;
  profileFieldsUpdated: number;
  jobChangesDetected: number;
  exportRequests: number;
  exportEmailsFound: number;
  extensionEnrichRequests: number;
}

export interface InsightsDay extends InsightsCounters {
  date: string;
}

export interface InsightsReport {
  days: InsightsDay[];
  totals: InsightsCounters;
  byUser: Array<{ user: string } & InsightsCounters>;
  linkedin: { replies: number; replyRate: number | null; acceptanceRate: number | null; queue: Record<string, number> };
}

// --------------------
// Phone enrichment settings (automatic or manual, rules)
// --------------------

export type PhoneNumberScope = "mobile" | "mobile_or_direct" | "all";
export type PhoneEnrichmentRuleKind =
  | "job_title_contains"
  | "has_open_call_task"
  | "in_sequence_with_call_step"
  | "in_any_active_sequence"
  | "owner_email_in";

export interface PhoneEnrichmentRule {
  id: string;
  kind: PhoneEnrichmentRuleKind;
  values?: string[];
  enabled: boolean;
}

export interface PhoneEnrichmentSettings {
  autoEnrich: boolean;
  numberScope: PhoneNumberScope;
  rules: PhoneEnrichmentRule[];
  manualEnabled: boolean;
  updatedAt?: number;
}

export const PHONE_ENRICHMENT_RULE_LABELS: Record<PhoneEnrichmentRuleKind, string> = {
  job_title_contains: "Title contains",
  has_open_call_task: "Has an open call task",
  in_sequence_with_call_step: "Is in an active sequence with a call step",
  in_any_active_sequence: "Is in any active sequence",
  owner_email_in: "Owner is one of",
};

export const PHONE_ENRICHMENT_RULE_HELP: Record<PhoneEnrichmentRuleKind, string> = {
  job_title_contains: "Only buy numbers for the titles you sell to. Matches any of the words you list.",
  has_open_call_task: "Someone is about to call this prospect and a missing number blocks them.",
  in_sequence_with_call_step: "Being worked in a sequence that dials. An all-email sequence never needs a number.",
  in_any_active_sequence: "Any prospect currently in a sequence.",
  owner_email_in: "Only prospects owned by these reps.",
};

// --------------------
// Export settings
// --------------------

export type ExportRuleOperator = "contains" | "doesNotContain" | "is" | "isNot" | "isEmpty" | "isNotEmpty" | "greaterThan" | "lessThan" | "between";

export interface ExportRuleCondition {
  entity: "person" | "company";
  field: string;
  operator: ExportRuleOperator;
  values: string[];
}

export interface ExportRuleBase {
  id: string;
  conditions: ExportRuleCondition[];
  match: "all" | "any";
}

export interface ExportTagRule extends ExportRuleBase {
  tags: string[];
}

export interface ExportSequenceRule extends ExportRuleBase {
  sequenceId: string;
  sequenceName?: string;
}

export interface ExportSequenceRouting {
  enabled?: boolean;
  defaultSequenceId?: string | null;
  defaultSequenceName?: string | null;
  rules?: ExportSequenceRule[];
}

export type ExportSequenceRoutingByCrm = Partial<Record<"outreach" | "hubspot", ExportSequenceRouting>>;

export type ExportFieldMaps = {
  outreach?: { prospect?: Record<string, string>; account?: Record<string, string> };
  hubspot?: { contact?: Record<string, string>; company?: Record<string, string> };
  salesforce?: { contact?: Record<string, string>; lead?: Record<string, string>; account?: Record<string, string> };
};

export interface ExportMappings {
  outreach?: { prospect?: Record<string, string>; account?: Record<string, string> };
  hubspot?: { contact?: Record<string, string>; company?: Record<string, string> };
  salesforce?: { contact?: Record<string, string>; lead?: Record<string, string>; account?: Record<string, string>; recordType?: "contact" | "lead" };
  overwriteExisting?: boolean;
  /** Outreach: tag every exported prospect "boring export · <rep email>". Off unless an admin turns it on. */
  tagExporter?: boolean;
  findEmail?: boolean;
  findPhone?: boolean;
  phoneNumberScope?: PhoneNumberScope;
  /** Fields reps cannot map, as "destination.record.field". */
  lockedFields?: string[];
  /** Reps' own tag rules are ignored. */
  lockTagRules?: boolean;
  /** Reps' own sequence routing is ignored. */
  lockSequenceRouting?: boolean;
  tagRules?: ExportTagRule[];
  sequenceRouting?: ExportSequenceRoutingByCrm;
  /** The stage a created or updated record gets, per CRM. Absent: exports leave the stage alone. */
  defaultStage?: ExportDefaultStageByCrm;
}

/** A stage as the CRM names it (HubSpot option value, Outreach stage id) and its label. */
export interface ExportStageChoice {
  value: string;
  label?: string | null;
}

export type ExportHubSpotStageProperty = "lifecyclestage" | "hs_lead_status";

export interface ExportDefaultStage {
  /** HubSpot only: Lifecycle stage (default) or Lead status. */
  property?: ExportHubSpotStageProperty;
  /** For records an export creates; null is "Don't change". */
  created?: ExportStageChoice | null;
  /** For existing records an export updates; null is "Don't change". */
  updated?: ExportStageChoice | null;
}

export type ExportDefaultStageByCrm = Partial<Record<"hubspot" | "outreach", ExportDefaultStage>>;

/** listExportStages: the stages the org's CRM offers. */
export interface ExportStagesResult {
  crm: string | null;
  hubspot?: Record<ExportHubSpotStageProperty, Array<{ value: string; label: string }>>;
  outreach?: Array<{ value: string; label: string }>;
  error?: string;
}


export interface ExportSequencesResult {
  crm: string | null;
  sequences: Array<{ id: string; name: string }>;
  error?: string;
}

/** A value an export can write (the backend's EXPORT_DATAPOINTS). */
export interface ExportDatapoint {
  key: string;
  label: string;
  group: string;
  description?: string;
  destinations?: string[];
}

export interface ExportSettingsResult {
  settings: ExportMappings;
  defaults: Record<string, Record<string, Record<string, string>>>;
  /** What a mapping can write, per person and company record. Missing from an older backend. */
  datapoints?: { person: ExportDatapoint[]; company: ExportDatapoint[] };
  organizationId: string;
  canFindPhone: boolean;
  /** Admins edit; reps see the same settings read-only. */
  canEdit?: boolean;
  /** Plan gating: Export needs the Enrich plan or above. */
  planRequired?: boolean;
  /** The caller's "boring exports · <rep>" HubSpot list, once their first export made it. */
  myExports?: { url: string | null; name: string } | null;
}

// --------------------
// LinkedIn conversations on the Outreach surfaces
// --------------------

export interface InboxConversation {
  id: string;
  linkedin_conversation_id: string;
  participant_name?: string | null;
  participant_vanity?: string | null;
  participant_picture_url?: string | null;
  last_message_text?: string | null;
  last_message_at?: string | null;
  last_message_is_outbound?: boolean;
  unread_count?: number;
  synced_by?: string | null;
  syncedByEmail?: string | null;
  syncedByName?: string | null;
  engagement?: { sent: boolean; seen: boolean; replies: number };
}

export interface InboxMedia {
  url?: string | null;
  mediaType?: string | null;
  type?: string | null;
  filename?: string | null;
  size?: number | null;
  duration?: number | null;
  assetUrn?: string | null;
  /** A post forwarded into the chat (mediaType "linkedin/post"). */
  postUrn?: string | null;
  /** Its preview, read by the extension with the rep's session when stored. */
  post?: SharedPostPreview | null;
}

export interface SharedPostPreview {
  authorName: string | null;
  authorHeadline: string | null;
  authorPictureUrl: string | null;
  authorUrl: string | null;
  text: string | null;
  imageUrl: string | null;
  imageWidth: number | null;
  imageHeight: number | null;
  /** Article or document title, when the post links out. */
  title: string | null;
  /** Article domain, or the document page count. */
  subtitle: string | null;
  kind?: string;
  fetchedAt?: number;
}

export interface InboxMessage {
  id: string;
  linkedin_message_id?: string | null;
  content?: string | null;
  is_outbound: boolean;
  sent_at: string;
  sender_name?: string | null;
  sender_linkedin_urn?: string | null;
  sender_picture_url?: string | null;
  media_urls?: InboxMedia[];
  /** "text", a media kind, or "deleted" for a message its sender deleted. */
  message_type?: string | null;
  reactions?: Array<{ emoji: string; count: number; viewerReacted?: boolean }>;
  reply_to?: { text?: string; messageUrn?: string; senderName?: string } | null;
  is_scheduled?: boolean;
  scheduled_at?: string;
}

/** Per-product automatic assignment rules, keyed by add-on. */
export type ProductLicenseRules = Record<string, { teams: string[]; roles: string[]; autoSync: boolean }>;

export interface ProfileEnrichmentSettings {
  /** Look the profile up even when the email was solved without one. */
  standaloneLookup: boolean;
  updatedAt?: number;
}
